import { useState } from 'react';
import { X } from 'lucide-react';
import { Trans, useTranslation } from 'react-i18next';
import { apriConsultazione } from '../../api/consultazioni';
import type { RichiestaDetail } from '../../api/richieste';

interface Props {
  richiestaId: string;
  onClose: () => void;
  onSaved: (updated: RichiestaDetail) => void;
}

const inputCls =
  'w-full text-sm border border-stone-border rounded px-3 py-1.5 focus:outline-none focus:border-sage focus:ring-1 focus:ring-sage/20 bg-white';

/**
 * Apre una consultazione (#96). Solo AMM/admin.
 * L'ordine va in `in_consultazione`; altre transizioni bloccate fino alla risposta.
 */
export default function ApriConsultazioneModal({ richiestaId, onClose, onSaved }: Props) {
  const { t } = useTranslation();
  const [tipo, setTipo] = useState<'RT' | 'DIR'>('RT');
  const [commento, setCommento] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleApri() {
    if (!commento.trim()) {
      setError(t('richieste.consultazione.val_motivo'));
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const updated = await apriConsultazione(richiestaId, {
        tipo,
        commento_richiesta: commento.trim(),
      });
      onSaved(updated);
    } catch (err: unknown) {
      const resp = (err as { response?: { status?: number; data?: { detail?: string } } })?.response;
      if (resp?.status === 409) {
        setError(resp.data?.detail ?? t('richieste.consultazione.errore_409'));
      } else {
        setError(resp?.data?.detail ?? t('richieste.consultazione.errore_apertura'));
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} aria-hidden="true" />
      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-md mx-4">
        <div className="flex items-center justify-between px-6 py-4 border-b border-stone-mid">
          <h2 className="text-base font-semibold text-ink-dark">{t('richieste.consultazione.titolo')}</h2>
          <button type="button" onClick={onClose} className="text-ink-soft hover:text-ink-dark">
            <X size={18} />
          </button>
        </div>

        <div className="px-6 py-5 space-y-4">
          <p className="text-sm text-ink-mid">
            <Trans i18nKey="richieste.consultazione.descrizione" components={{ strong: <strong /> }} />
          </p>

          <div>
            <label className="block text-xs font-medium text-ink-soft mb-2">{t('richieste.consultazione.consulta')}</label>
            <div className="grid grid-cols-2 gap-2">
              {(['RT', 'DIR'] as const).map((tipoOpt) => (
                <button
                  key={tipoOpt}
                  type="button"
                  onClick={() => setTipo(tipoOpt)}
                  className={`px-3 py-2 text-sm font-medium rounded-lg border transition-colors ${
                    tipo === tipoOpt
                      ? 'bg-sage text-white border-sage'
                      : 'bg-white text-ink-mid border-stone-border hover:bg-stone-bg'
                  }`}
                >
                  {tipoOpt === 'RT' ? t('richieste.consultazione.opt_rt') : t('richieste.consultazione.opt_dir')}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-ink-soft mb-1">
              {t('richieste.consultazione.motivo')} <span className="text-accent-danger">*</span>
            </label>
            <textarea
              className={inputCls}
              rows={4}
              value={commento}
              onChange={(e) => setCommento(e.target.value)}
              placeholder={t('richieste.consultazione.motivo_placeholder')}
            />
          </div>

          {error && (
            <div className="px-3 py-2 bg-accent-danger-bg border border-[#FECACA] rounded text-sm text-accent-danger">
              {error}
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-stone-mid bg-stone-bg">
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="px-4 py-2 text-sm font-medium text-ink-mid border border-stone-border rounded-lg hover:bg-white transition-colors disabled:opacity-50"
          >
            {t('common.azioni.annulla')}
          </button>
          <button
            type="button"
            onClick={handleApri}
            disabled={loading || !commento.trim()}
            className="px-4 py-2 text-sm font-medium text-white bg-sage rounded-lg hover:bg-[#5C6F59] transition-colors disabled:opacity-60"
          >
            {loading ? t('richieste.consultazione.apertura_in_corso') : t('richieste.consultazione.btn_apri', { tipo })}
          </button>
        </div>
      </div>
    </div>
  );
}
